import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { AlertTriangle, X } from "lucide-react";

interface GeminiStatus {
  status: "ok" | "degraded";
  checked_at: string;
  reason: DegradedReason | null;
}

// Imported, not restated: this was a hand-copied union and it went stale the
// moment the shared classifier gained a reason.
type DegradedReason = import(
  "../../../../../supabase/functions/_shared/geminiFailure.ts"
).DegradedReason;

/**
 * What to actually tell someone.
 *
 * Every cause used to render as "experiencing issues … try again later", which
 * is wrong twice over for the two most common ones: an exhausted quota and an
 * invalid key are not transient, and trying again later does nothing. Both are
 * the operator's to fix, so say so.
 *
 * These strings are derived from a fixed enum, never from provider text —
 * `error_detail` can contain the API key and stays server-side.
 */
const REASON_MESSAGE: Record<DegradedReason, string> = {
  // Deliberately does not say "top up and it works". A balance only counts if
  // it is on the same Google Cloud project as the API key, and that project is
  // on the paid tier — topping up a different project changes nothing, which
  // is a confusing thing to discover after paying.
  quota: "Gemini's quota for this project is spent. A billing balance only lifts this if it sits on the same Google Cloud project as the API key.",
  spend_cap: "The Google Cloud project has hit its monthly spend cap for the Gemini API. Raise it at ai.studio/spend — adding credits does not lift a cap.",
  rate_limit: "Too many requests to Gemini in a short window. This clears by itself — try again shortly.",
  auth: "The Gemini API key is invalid or expired. Generation will fail until it is replaced.",
  no_image: "Gemini is accepting requests but returning no images. This usually clears on its own — try again shortly.",
  timeout: "Gemini is not responding. Generation may be slow or fail — try again shortly.",
  network: "Gemini is unreachable from the server. Generation will fail until the connection recovers.",
  unknown: "Image generation is failing for an unknown reason. The details are in the server logs.",
};


export function GeminiStatusBanner() {
  const [status, setStatus] = useState<GeminiStatus | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Initial fetch
    supabase
      .from("gemini_status" as any)
      .select("status, checked_at, reason")
      .eq("id", 1)
      .single()
      .then(({ data }) => {
        if (data) setStatus(data as unknown as GeminiStatus);
      });

    // Realtime subscription
    const channel = supabase
      .channel("gemini-status-changes")
      .on(
        "postgres_changes" as any,
        { event: "UPDATE", schema: "public", table: "gemini_status" },
        (payload: any) => {
          const newStatus = payload.new as GeminiStatus;
          setStatus(newStatus);
          // Re-show banner if status changed to degraded
          if (newStatus.status === "degraded") {
            setDismissed(false);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // Don't show if no data, status is ok, or user dismissed
  if (!status || status.status === "ok" || dismissed) return null;

  // Only show if checked within last 20 minutes
  const checkedAt = new Date(status.checked_at).getTime();
  const twentyMinAgo = Date.now() - 20 * 60 * 1000;
  if (checkedAt < twentyMinAgo) return null;

  return (
    <div className="bg-amber-50 border-b border-amber-200 px-4 py-2.5 flex items-center justify-between text-sm text-amber-800">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
        <span>
          {REASON_MESSAGE[status.reason ?? "unknown"]}
        </span>
      </div>
      <button
        onClick={() => setDismissed(true)}
        className="ml-4 p-1 rounded hover:bg-amber-100 transition-colors shrink-0"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
